// src/components/WaiverDialog.js
// Reusable waiver/acceptance dialog. Renders a clubDocument's content and
// the appropriate input (checkbox for 'accept', typed signature for
// 'signature'), then fires onAccepted with the built acceptance entry.
//
// This component does NOT write to Firestore itself — the caller decides
// what to do with the entry (write to user doc, attach to a signup write,
// include in a Stripe Checkout metadata, etc).
//
// Props:
//   open         boolean         dialog open state
//   doc          object|null     the clubDocument: { id, name, content, actionType, expirationDays, ... }
//   onClose      () => void      fires on cancel / backdrop click
//   onAccepted   (entry, ctx) => Promise<void>  fires after user accepts; entry is
//                                ready to write under documentAcceptances[doc.id].
//                                ctx contains { signature } for callers that need raw input.
//                                Caller's promise controls the saving spinner;
//                                throw to surface the error.
//   busy         boolean         optional override of saving spinner (when caller
//                                is doing extra work after onAccepted resolves)

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Checkbox, FormControlLabel, TextField, Typography, Alert, Box,
} from '@mui/material';
import { buildAcceptanceEntry } from '../utils/waiverGate';

export default function WaiverDialog({
  open,
  doc: docRecord,
  onClose,
  onAccepted,
  busy = false,
}) {
  const [checked, setChecked] = useState(false);
  const [signature, setSignature] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Reset internal state every time the dialog opens for a fresh doc.
  useEffect(() => {
    if (open) {
      setChecked(false);
      setSignature('');
      setError('');
      setSaving(false);
    }
  }, [open, docRecord?.id]);

  const isSignature = docRecord?.actionType === 'signature';

  async function handleAccept() {
    if (!docRecord) return;
    if (isSignature) {
      if (!signature.trim()) {
        setError('Type your full name to sign.');
        return;
      }
    } else if (!checked) {
      setError('Check the box to confirm you have read and accepted the document.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const entry = buildAcceptanceEntry(docRecord, { signature });
      // Hand the caller the ready-to-write entry plus raw inputs. Awaiting
      // here keeps the spinner up until the caller finishes whatever follow-on
      // work it has (e.g. write the user doc, then redirect to Stripe).
      await onAccepted(entry, { signature: signature.trim() });
    } catch (err) {
      console.error('Waiver acceptance failed:', err);
      setError(err?.message || 'Failed to record acceptance. Please try again.');
      setSaving(false);
      return;
    }
    // We deliberately leave `saving` true after success — the caller will
    // typically unmount us by closing the dialog or redirecting to Stripe.
  }

  const showBusy = saving || busy;

  return (
    <Dialog
      open={open}
      onClose={() => !showBusy && onClose && onClose()}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ backgroundColor: '#1E3A5F', color: 'white' }}>
        {docRecord?.name || 'Required Waiver'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }} dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
          {docRecord?.expirationDays > 0
            ? `Your acceptance will be valid for ${docRecord.expirationDays} days.`
            : 'Your acceptance does not expire.'}
        </Typography>
        {/* Rendering admin-authored HTML from clubDocuments. Content is
            trusted (admin-only write). If ever exposed to untrusted authors,
            sanitize through DOMPurify before this point. */}
        <Box
          sx={{
            '& p': { margin: '0.5em 0' },
            '& h1,h2,h3': { color: '#1E3A5F' },
            fontSize: '0.95rem',
            color: '#222',
          }}
          dangerouslySetInnerHTML={{ __html: docRecord?.content || '<em>No content.</em>' }}
        />
        <Box sx={{ mt: 3 }}>
          {isSignature ? (
            <TextField
              fullWidth
              label="Type your full name to sign"
              value={signature}
              onChange={e => setSignature(e.target.value)}
              placeholder="Your full legal name"
              disabled={showBusy}
              sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}
            />
          ) : (
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={e => setChecked(e.target.checked)}
                  disabled={showBusy}
                />
              }
              label="I have read and accept the agreement above."
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={showBusy}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={showBusy}
          sx={{ backgroundColor: '#1E3A5F' }}
        >
          {showBusy ? 'Saving…' : 'Accept & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
